import React, { useCallback, useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { FiEdit2, FiPlusCircle } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as inventoryService from "../../services/inventoryService";
import { getRole } from "../../components/Apicall";
import InventoryTable from "./InventoryTable";
import LowStockBadge from "./LowStockBadge";
import ItemFormDrawer from "./ItemFormDrawer";
import StockAdjustModal from "./StockAdjustModal";
import InventoryDashboardWidget from "../../components/InventoryAnalytics/InventoryDashboardWidget";
import { formatDecimal } from "../../utils/numberFormat";
import { getVendorCurrency } from "../../utils/currency";

const rowActionBtnStyle = {
  border: "1px solid #E5E7EB",
  borderRadius: "8px",
  padding: "6px 10px",
  color: "#6B7280",
  background: "#fff",
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  fontSize: "13px",
  fontWeight: "600",
};

const primaryRowActionBtnStyle = {
  ...rowActionBtnStyle,
  background: "var(--button-color)",
  border: "none",
  color: "#fff",
};

function InventoryItemsList() {
  const navigate = useNavigate();
  const isAdmin = getRole() === "3";

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [stockStatus, setStockStatus] = useState("");
  const [page, setPage] = useState(1);

  const [formItem, setFormItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [adjustItem, setAdjustItem] = useState(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inventoryService.getItems({
        search,
        category_id: categoryFilter,
        stock_status: stockStatus,
        page,
        limit: 20,
      });
      setItems(res?.data || []);
    } catch (err) {
      toast.error(err.message || "Could not load items");
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, stockStatus, page]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    inventoryService.getCategories().then((res) => setCategories(res?.data || []));
  }, []);

  const columns = [
    { key: "name", label: "Item", render: (row) => `${row.name} (${row.sku})` },
    { key: "category_name", label: "Category", render: (row) => row.category_name || "Uncategorized" },
    { key: "current_qty", label: "Qty", render: (row) => `${formatDecimal(row.current_qty)} ${row.unit_of_measure}` },
    {
      key: "status",
      label: "Status",
      render: (row) => <LowStockBadge currentQty={row.current_qty} reorderLevel={row.reorder_level} />,
    },
    { key: "selling_price", label: "Selling price", render: (row) => `${getVendorCurrency()} ${formatDecimal(row.selling_price)}` },
    ...(isAdmin
      ? [
          {
            key: "actions",
            label: "",
            render: (row) => (
              <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  style={rowActionBtnStyle}
                  onClick={() => {
                    setFormItem(row);
                    setShowForm(true);
                  }}
                >
                  <FiEdit2 size={14} /> Edit
                </button>
                <button type="button" style={primaryRowActionBtnStyle} onClick={() => setAdjustItem(row)}>
                  <FiPlusCircle size={14} /> Stock-In/Adjust
                </button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="p-3">
      <InventoryDashboardWidget />

      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h4 className="mb-0">Inventory Items</h4>
        {isAdmin && (
          <Button
            style={{ background: "var(--button-color)", border: "none", color: "#fff" }}
            onClick={() => {
              setFormItem(null);
              setShowForm(true);
            }}
          >
            + New Item
          </Button>
        )}
      </div>

      <div className="d-flex gap-2 mb-3 flex-wrap">
        <Form.Select className="inventory-select" style={{ maxWidth: 200 }} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Form.Select>
        <Form.Select className="inventory-select" style={{ maxWidth: 200 }} value={stockStatus} onChange={(e) => setStockStatus(e.target.value)}>
          <option value="">All stock levels</option>
          <option value="low_stock">Low stock</option>
          <option value="out_of_stock">Out of stock</option>
        </Form.Select>
      </div>

      <InventoryTable
        columns={columns}
        data={items}
        loading={loading}
        searchQuery={search}
        onSearchChange={(v) => {
          setPage(1);
          setSearch(v);
        }}
        currentPage={page}
        onPageChange={setPage}
        onRowClick={(row) => navigate(`/inventory/${row.id}`)}
        emptyMessage="No inventory items yet."
      />

      <ItemFormDrawer
        show={showForm}
        item={formItem}
        onHide={() => setShowForm(false)}
        onSaved={loadItems}
      />
      <StockAdjustModal
        show={!!adjustItem}
        item={adjustItem}
        onHide={() => setAdjustItem(null)}
        onPosted={loadItems}
      />
    </div>
  );
}

export default InventoryItemsList;
